import { OpenAPI } from './client/requests'

/**
 * Fires a DELETE while the page is going away.
 *
 * The normal discard path runs when the user navigates inside the app: `LeaveDraftsGuard`
 * shows its dialog, awaits the delete, then lets the navigation through. A reload or a
 * closed tab never reaches it — that goes through the browser's own "leave site?" prompt,
 * which has no callback and cannot await anything, so the drafts were simply left behind.
 *
 * Two deliberate choices make this safe to attempt at all:
 *
 * `keepalive` lets the request outlive the document. Without it the browser cancels it the
 * moment the page unloads, which is precisely when it is sent.
 *
 * The header is read from `OpenAPI.HEADERS` rather than minted. Getting a fresh token is
 * async and there is no time left for that; the request interceptor keeps this one current
 * after every call, so it is the newest token the app has seen.
 *
 * Best-effort by nature — the browser may drop it, and it is silent when it does. The
 * nightly sweep is what actually guarantees these get cleaned up.
 */
export const discardOnUnload = (path: string) => {
  const authorization = (OpenAPI.HEADERS as Record<string, string> | undefined)?.Authorization

  if (!OpenAPI.BASE || !authorization) return

  void fetch(`${OpenAPI.BASE}${path}`, {
    headers: { Authorization: authorization },
    keepalive: true,
    method: 'DELETE',
  }).catch(() => {
    // Nothing to do and nobody to tell: the page is already gone.
  })
}
